import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ArrowLeft, Check, CheckCircle2, Download, FilePen, FileText, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { api, mensajeError } from '../lib/api';
import {
  useTelefonoClinica, tipoDeFallo, cuandoReintentar, aQuienLlamar, conMayuscula,
} from '../lib/enlacePaciente';
import { telefonoParaEnlace } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import FirmaDedo from '../components/FirmaDedo';
import VisorPdfPublico from '../components/VisorPdfPublico';
import LogoClinica from '../components/LogoClinica';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Desde la guía «mis pasos» se llega aquí con `volver=/mis-pasos/<token>#…`
 * (2026-09-03): al terminar, el paciente vuelve a sus pasos. Solo se admite
 * esa ruta propia, nunca una URL cualquiera.
 */
function rutaDeVuelta(valor) {
  return /^\/mis-pasos\/[A-Za-z0-9._~-]+(#[a-z]+)?$/.test(valor || '') ? valor : '';
}

const SIN_CONEXION = 'No hemos podido conectar con la clínica. Comprueba tu conexión y vuelve a intentarlo';

/**
 * UN CORTE NO ES UN ENLACE MUERTO (23/9/2026; dirección: «no queremos tener una
 * rotura de conexión»). Cualquier fallo al pedir el estado se pintaba «Este
 * enlace no es válido o ha caducado»: sin cobertura, un 502 del servidor o un
 * timeout, la paciente leía que su enlace estaba muerto y no volvía. Qué ha
 * pasado lo decide `tipoDeFallo`, la misma regla que las otras páginas del
 * paciente (lib/enlacePaciente):
 *  - 404 / 410: el enlace no vale, con el texto del servidor si lo trae;
 *  - 422: el enlace se cortó al copiarlo;
 *  - 429: el freno por IP (2ª pasada del 23/9). Se pintaba como un corte y
 *    «Reintentar» volvía a dar 429: se dice cuánto esperar, sin botón;
 *  - lo demás (sin respuesta, timeout, 5xx): no hemos podido hablar con la
 *    clínica, y se puede reintentar.
 */
function falloDeCarga(e) {
  const tipo = tipoDeFallo(e);
  if (tipo === 'invalido') {
    const detalle = e?.response?.data?.detail;
    return {
      tipo: 'enlace',
      texto: typeof detalle === 'string' && detalle.trim() ? detalle.trim() : 'Este enlace no es válido o ha caducado',
    };
  }
  if (tipo === 'incompleto') {
    return { tipo: 'enlace', texto: 'El enlace está incompleto: ábrelo desde el mensaje completo que te enviamos' };
  }
  if (tipo === 'espera') {
    return {
      tipo: 'espera',
      texto: `Hay mucho movimiento desde tu conexión. Tu enlace sigue siendo válido: vuelve a abrirlo ${cuandoReintentar(e)}`,
    };
  }
  return { tipo: 'conexion', texto: SIN_CONEXION };
}

/**
 * La pantalla que toca según el `estado` del enlace (revisión de enlaces
 * 2026-09-07): «firmado» → hecho; «cancelado» o «caducado» → anulado.
 */
function pasoDe(datos) {
  const estado = datos?.estado;
  if (estado === 'firmado' || (!estado && datos?.done)) return 'done';
  if (estado === 'cancelado' || estado === 'caducado') return 'anulado';
  return 'review';
}

/**
 * Las descargas de lo que la paciente ya firmó (`signed` del estado del
 * enlace). Las mismas en «firmado» y en «anulado» (2ª pasada del 22/9): con
 * la cirugía anulada, lo firmado sigue siendo su copia.
 */
function DescargasFirmadas({ signed }) {
  return (
    <div className="flex flex-col gap-2 max-w-sm mx-auto">
      {(signed || []).map((item) => (
        <a
          key={item.consent_id}
          href={`${BACKEND_URL}${item.pdf_url}`}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="inline-flex items-center justify-center h-10 rounded-md bg-[#2E6E5E] px-4 text-sm font-medium text-white hover:bg-[#1E4A3F]"
          data-testid="consent-link-descargar"
        >
          <Download className="w-4 h-4 mr-2" /> Descargar {item.name}
        </a>
      ))}
    </div>
  );
}

/**
 * Lo que queda por firmar en el enlace, empezando por el que se enseña
 * (`por_firmar` del estado, 2/10/2026). Un servidor anterior no lo manda:
 * entonces solo se sabe del que está delante.
 */
function porFirmarDe(datos) {
  if (Array.isArray(datos?.por_firmar)) return datos.por_firmar;
  const p = datos?.pending;
  return p ? [{ consent_id: p.consent_id, name: p.template?.name || 'Consentimiento' }] : [];
}

/** «Te queda 1» / «Te quedan 3». */
const teQuedan = (n) => `Te ${n === 1 ? 'queda' : 'quedan'} ${n}`;

/**
 * Lo que se dice al guardar una firma: y, si queda otro, cuántos y cuál
 * toca ahora (2/10/2026). La pantalla pasa sola al siguiente documento y,
 * con el mismo aspecto, parecía que no había cambiado nada.
 */
function avisoDeFirma(base, datos) {
  const quedan = porFirmarDe(datos).length;
  const siguiente = datos?.pending?.template?.name;
  if (!quedan || !siguiente) return base;
  return `${base} ${teQuedan(quedan)} por firmar: ahora lee y firma «${siguiente}».`;
}

/**
 * VARIOS DOCUMENTOS EN EL ENLACE, Y QUE SE VEA (2/10/2026, la clínica: «los
 * pacientes firman el primero y no se dan cuenta de que hay más
 * pendientes»). El enlace los sirve de uno en uno y, firmado uno, enseña el
 * siguiente con la misma cara: no había nada que dijera cuántos eran. Con
 * dos o más, arriba: cuántos quedan, la barra con un trozo por documento y
 * la lista con el estado de cada uno (Firmado / Ahora / Pendiente). Con uno
 * solo, nada: la página de siempre.
 */
function ProgresoDelEnlace({ signed, porFirmar }) {
  const total = signed.length + porFirmar.length;
  if (total < 2 || porFirmar.length === 0) return null;
  const filas = [
    ...signed.map((s) => ({ id: s.consent_id, name: s.name, estado: 'firmado' })),
    ...porFirmar.map((p, i) => ({ id: p.consent_id, name: p.name, estado: i === 0 ? 'ahora' : 'pendiente' })),
  ];
  const etiqueta = { firmado: 'Firmado', ahora: 'Ahora', pendiente: 'Pendiente' };
  return (
    <div className="rounded-lg border border-[#2E6E5E]/30 bg-[#F6F8F7] p-3" data-testid="consent-link-progreso">
      <div className="flex items-start justify-between gap-3">
        <p className="text-base font-semibold text-[#13231E] leading-snug" data-testid="consent-link-quedan">
          {teQuedan(porFirmar.length)} {porFirmar.length === 1 ? 'documento' : 'documentos'} por firmar
        </p>
        <span className="shrink-0 pt-0.5 text-sm font-semibold text-[#13231E]" data-testid="consent-link-contador">
          {signed.length} de {total} firmados
        </span>
      </div>
      <div className="mt-2 flex gap-1" aria-hidden="true">
        {filas.map((f, i) => (
          <span
            key={f.id || i}
            className={`h-2 flex-1 rounded-full ${
              f.estado === 'firmado' ? 'bg-emerald-600' : f.estado === 'ahora' ? 'bg-[#2E6E5E]/40' : 'bg-border'
            }`}
          />
        ))}
      </div>
      <ol className="mt-3 space-y-1.5">
        {filas.map((f, i) => (
          <li
            key={f.id || i}
            className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm ${
              f.estado === 'ahora' ? 'bg-white ring-1 ring-[#2E6E5E]/40' : ''
            }`}
            data-testid="consent-link-progreso-doc"
            data-estado={f.estado}
            aria-current={f.estado === 'ahora' ? 'step' : undefined}
          >
            <span
              className={`shrink-0 w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold ${
                f.estado === 'firmado' ? 'bg-emerald-600 text-white'
                  : f.estado === 'ahora' ? 'bg-[#2E6E5E] text-white' : 'bg-muted text-muted-foreground'
              }`}
              aria-hidden="true"
            >
              {f.estado === 'firmado' ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span className={`flex-1 min-w-0 break-words text-[#13231E] ${f.estado === 'ahora' ? 'font-semibold' : ''}`}>
              {f.name}
            </span>
            <span
              className={`shrink-0 text-[11px] font-semibold uppercase tracking-wide ${
                f.estado === 'firmado' ? 'text-emerald-700' : f.estado === 'ahora' ? 'text-[#2E6E5E]' : 'text-amber-700'
              }`}
            >
              {etiqueta[f.estado]}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * Lo que la clínica ha retirado de este enlace (3ª pasada del 23/9): va
 * aparte en el estado (`retirados`) y no cuenta ni como firmado ni como
 * anulado. Se dice, para que no lo busque ni crea que falta.
 */
function Retirados({ retirados }) {
  const nombres = (retirados || []).map((r) => r.name).filter(Boolean);
  if (!nombres.length) return null;
  return (
    <p className="text-sm text-muted-foreground" data-testid="consent-link-retirados">
      {nombres.length === 1
        ? `Este documento se ha retirado: «${nombres[0]}». No hace falta que lo firmes.`
        : `Estos documentos se han retirado: ${nombres.map((n) => `«${n}»`).join(', ')}. No hace falta que los firmes.`}
    </p>
  );
}

export default function PublicConsentLinkPage() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const volver = rutaDeVuelta(params.get('volver'));
  const [data, setData] = useState(null);
  // `fallo` (23/9/2026) = no se ha podido cargar el estado: {tipo: 'enlace' |
  // 'conexion', texto, firmaGuardada?}. Sustituía la tarjeta entera con un
  // único texto, «no es válido», fuera cual fuera el fallo.
  const [fallo, setFallo] = useState(null);
  // El teléfono de la clínica, de la misma fuente que las otras páginas del
  // paciente (`lib/enlacePaciente`: Configuración, o el último que leyó bien
  // este dispositivo). Puede no saberse: entonces no se inventa ninguno y se
  // dice «llama a la clínica» (`aQuienLlamar`). Solo hace falta si algo falla.
  const telefonoClinica = useTelefonoClinica('', Boolean(fallo));
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [step, setStep] = useState('review');
  // Aviso de la firma (vacía o no guardada), pintado DEBAJO del recuadro. Iba
  // a `error`, que sustituye la tarjeta entera: al pulsar «Firmar» sin haber
  // firmado desaparecía el recuadro y el paciente se quedaba sin salida
  // (revisión de enlaces 2026-09-07).
  const [avisoFirma, setAvisoFirma] = useState('');
  // Lo que pasó con la firma anterior, encima del documento que toca ahora
  // (23/9/2026): «Firma de «Blefaroplastia» guardada» cuando se pasa al
  // siguiente, o el motivo si el que estaba firmando ya no se puede firmar.
  const [aviso, setAviso] = useState(null);
  // El documento (`consent_id`) cuyo PDF se ha abierto en esta página (3ª
  // pasada del 23/9): el mismo criterio que el iPad, el `load` del visor o el
  // botón «Abrir el documento completo». Sin él no se pasa a firmar, y el
  // servidor tampoco lo aceptaría («Abre y lee el documento antes…»).
  const [pdfAbierto, setPdfAbierto] = useState('');
  const sigRef = useRef(null);

  const load = useCallback(async () => {
    if (!token) {
      setFallo({ tipo: 'enlace', texto: 'Enlace no válido' });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/public/consent-link/${encodeURIComponent(token)}`);
      setData(response.data);
      setStep(pasoDe(response.data));
      setFallo(null);
    } catch (e) {
      setFallo(falloDeCarga(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  /** El estado, sin tocar la pantalla ni el recuadro de la firma. */
  const pedirEstado = async () => {
    try {
      const response = await api.get(`/api/public/consent-link/${encodeURIComponent(token)}`);
      return { datos: response.data };
    } catch (e) {
      return { error: e };
    }
  };

  /**
   * LA FIRMA DICE QUÉ DOCUMENTO FIRMA (23/9/2026, ALTO del verificador). No
   * lo decía: el servidor firmaba «el primero pendiente». Con la conexión
   * mala, la firma de «Blefaroplastia» se guardaba, la respuesta se perdía,
   * la pantalla seguía en «Blefaroplastia», la paciente volvía a pulsar… y
   * quedaba firmada «Anestesia», que no había abierto. Ahora se manda el
   * `consent_id` que se enseña (el servidor firma ESE, o contesta «ya estaba
   * firmado») y tras CUALQUIER error se vuelve a preguntar el estado, no solo
   * tras el 410:
   *  - si la firma llegó, se dice y se pasa al siguiente para LEERLO;
   *  - si el servidor sigue con el mismo documento, se queda en la firma con
   *    el aviso, sin perder el trazo;
   *  - si ni el estado responde, no se sabe si llegó: se dice, y volver a
   *    pulsar es seguro (con el `consent_id` no se firma dos veces).
   * Y tras una firma BUENA, si la recarga falla, la firma se guardó y se
   * dice: eso no se puede convertir en «enlace no válido».
   */
  const firmar = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setAvisoFirma('Firme dentro del recuadro antes de continuar.');
      return;
    }
    const documento = data?.pending;
    if (!documento) return;
    const nombre = documento.template?.name || 'el consentimiento';
    setSigning(true);
    setAvisoFirma('');
    setAviso(null);
    let respuesta = null;
    let errorFirma = null;
    try {
      respuesta = await api.post(`/api/public/consent-link/${encodeURIComponent(token)}/sign`, {
        signature_b64: sigRef.current.getCanvas().toDataURL('image/png'),
        consent_id: documento.consent_id,
      });
    } catch (e) {
      errorFirma = e;
    }
    const { datos, error } = await pedirEstado();
    if (!errorFirma) {
      const guardada = respuesta?.data?.ya_firmado
        ? `«${nombre}» ya estaba firmado.` : `Firma de «${nombre}» guardada.`;
      if (!datos) {
        // La firma se guardó: la recarga que falla nunca la convierte en
        // «enlace no válido». Si es el freno (429), se dice cuánto esperar.
        const f = falloDeCarga(error);
        setFallo({ ...(f.tipo === 'enlace' ? { tipo: 'conexion', texto: SIN_CONEXION } : f), firmaGuardada: nombre });
        setAviso({ tono: 'ok', texto: guardada });
      } else {
        setData(datos);
        setStep(pasoDe(datos));
        setFallo(null);
        if (pasoDe(datos) === 'review') setAviso({ tono: 'ok', texto: avisoDeFirma(guardada, datos) });
      }
    } else if (!datos) {
      const tipo = tipoDeFallo(error);
      if (tipo === 'invalido') {
        setFallo(falloDeCarga(error));
      } else if (tipo === 'espera') {
        setAvisoFirma(`Hay mucho movimiento desde tu conexión. Vuelve a pulsar «Firmar» ${cuandoReintentar(error)}: `
          + 'si tu firma ya se guardó, no se firmará dos veces.');
      } else {
        setAvisoFirma('No hemos podido confirmar si tu firma ha llegado. Comprueba tu conexión y '
          + 'vuelve a pulsar «Firmar»: si ya se guardó, no se firmará dos veces.');
      }
    } else {
      const firmadoYa = (datos.signed || []).some((s) => s.consent_id === documento.consent_id);
      const mismo = !firmadoYa && pasoDe(datos) === 'review'
        && datos.pending?.consent_id === documento.consent_id;
      setData(datos);
      setFallo(null);
      if (mismo && errorFirma?.response?.status === 409) {
        // El servidor pide que lo lea antes (no le consta abierto aquí, o
        // ha cambiado desde que lo leyó): vuelve a la lectura, a abrirlo.
        setStep('review');
        setPdfAbierto('');
        setAviso({ tono: 'info', texto: mensajeError(errorFirma, 'Abre y lee el documento antes de firmarlo.') });
      } else if (mismo && tipoDeFallo(errorFirma) === 'espera') {
        // El freno (429): cuánto esperar, con el trazo en su sitio.
        setAvisoFirma(`Hay mucho movimiento desde tu conexión. Vuelve a pulsar «Firmar» ${cuandoReintentar(errorFirma)}.`);
      } else if (mismo) {
        // Sigue delante del mismo documento: la firma no llegó. Se queda en
        // el recuadro, con su trazo y el aviso.
        setAvisoFirma(mensajeError(errorFirma, 'No se pudo guardar la firma.'));
      } else {
        // La cirugía se anuló (410), el documento ya no se firma, o la firma
        // SÍ llegó: manda lo que dice el servidor (2ª pasada del 22/9).
        setStep(pasoDe(datos));
        if (pasoDe(datos) === 'review') {
          setAviso(firmadoYa
            ? { tono: 'ok', texto: avisoDeFirma(`Firma de «${nombre}» guardada.`, datos) }
            : { tono: 'info', texto: mensajeError(errorFirma, `«${nombre}» ya no está pendiente de firma.`) });
        }
      }
    }
    setSigning(false);
  };

  const pending = data?.pending;
  // Cuántos documentos lleva el enlace y cuántos quedan (2/10/2026).
  const firmadosDelEnlace = data?.signed || [];
  const porFirmar = porFirmarDe(data);
  const totalDelEnlace = firmadosDelEnlace.length + porFirmar.length;
  const pdfListo = !!pending && pdfAbierto === pending.consent_id;
  const pdfUrl = pending
    ? `${BACKEND_URL}/api/public/consent-link/${encodeURIComponent(token)}/pdf/${pending.consent_id}`
    : '';

  return (
    <div className="min-h-screen bg-[#F4F6FB] text-foreground p-4 sm:p-8">
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="bg-white border border-border rounded-lg px-5 py-4 flex items-center gap-3">
          {/* El logo de Configuración o, sin él, el nombre de la clínica. */}
          <LogoClinica className="h-10 w-auto" conNombre />
          <div>
            <h1 className="text-[#13231E] font-display font-bold text-lg">Consentimiento informado</h1>
            <p className="text-xs text-muted-foreground">Firma segura desde el móvil</p>
          </div>
        </header>

        {loading ? (
          <Card><CardContent className="py-16 flex items-center justify-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Cargando documento…</CardContent></Card>
        ) : fallo ? (
          <Card data-testid="consent-link-fallo">
            <CardContent className="py-12 text-center space-y-3">
              {fallo.firmaGuardada ? (
                <p className="font-medium text-emerald-700 flex items-center justify-center gap-2" data-testid="consent-link-firma-guardada">
                  <CheckCircle2 className="w-5 h-5 shrink-0" /> Tu firma de «{fallo.firmaGuardada}» se ha guardado.
                </p>
              ) : (
                <XCircle className="w-10 h-10 text-red-500 mx-auto" />
              )}
              <p className="font-medium" data-testid="consent-link-fallo-texto">{fallo.texto}</p>
              {fallo.tipo === 'conexion' && (
                <Button variant="outline" className="h-11" onClick={() => load()} data-testid="consent-link-reintentar">
                  <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
                </Button>
              )}
              <p className="text-sm text-muted-foreground" data-testid="consent-link-telefono">
                {telefonoClinica ? (
                  <>
                    {fallo.tipo === 'enlace'
                      ? 'Llama a la clínica y te lo volvemos a enviar: '
                      : 'Si sigue sin funcionar, llama a la clínica: '}
                    {/* El número entero en una línea: partido en el móvil no
                        se puede pulsar ni copiar bien. */}
                    <a href={`tel:${telefonoParaEnlace(telefonoClinica)}`} className="whitespace-nowrap underline">{telefonoClinica}</a>.
                  </>
                ) : fallo.tipo === 'enlace'
                  ? `${conMayuscula(aQuienLlamar(''))} y te lo volvemos a enviar.`
                  : `Si sigue sin funcionar, ${aQuienLlamar('')}.`}
              </p>
            </CardContent>
          </Card>
        ) : step === 'anulado' ? (
          <Card data-testid="consent-link-anulado">
            <CardContent className="p-6 sm:p-8 text-center space-y-4">
              <XCircle className="w-14 h-14 text-amber-500 mx-auto" />
              {/* Qué se le dice depende de POR QUÉ no hay nada que firmar
                  (2ª pasada del 22/9). Con la cirugía anulada o en «no acude»
                  el servidor manda el `mensaje` de la guía: se enseña tal
                  cual, para que no lea una cosa aquí y otra en la guía. Antes
                  salía siempre «ha sido anulado, pida uno nuevo en la guía»,
                  y era falso: la guía también está cerrada. Sin papel
                  (plantilla retirada) nadie lo anuló y otro enlace no lo
                  arregla. Solo si recepción lo anuló se le invita a pedir otro. */}
              {data?.mensaje ? (
                <>
                  <h2 className="text-2xl font-display font-semibold text-[#13231E]">Este enlace de firma ya no está activo</h2>
                  <p className="text-sm text-muted-foreground" data-testid="consent-link-mensaje">{data.mensaje}</p>
                </>
              ) : data?.motivo === 'sin_papel' ? (
                <>
                  <h2 className="text-2xl font-display font-semibold text-[#13231E]">Este documento no está disponible ahora mismo</h2>
                  <p className="text-sm text-muted-foreground">La clínica tiene que revisarlo. Llámenos y se lo resolvemos; no hace falta pedir otro enlace.</p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-display font-semibold text-[#13231E]">Este enlace de firma ha sido anulado</h2>
                  <p className="text-sm text-muted-foreground">Vuelva a su guía de cirugía para pedir uno nuevo, o llámenos y se lo reenviamos.</p>
                </>
              )}
              {/* Lo que ya firmó sigue siendo suyo: el servidor sigue dando
                  su PDF, y aquí no se veía (2ª pasada del 22/9). */}
              {(data?.signed || []).length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Lo que ya firmaste queda guardado:</p>
                  <DescargasFirmadas signed={data.signed} />
                </div>
              )}
              <Retirados retirados={data?.retirados} />
              {volver && (
                <a
                  href={volver}
                  className="inline-flex items-center justify-center h-10 rounded-md border border-[#2E6E5E] px-4 text-sm font-medium text-[#13231E] hover:bg-[rgba(46,110,94,0.08)]"
                  data-testid="consent-link-volver"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Volver a mis pasos
                </a>
              )}
            </CardContent>
          </Card>
        ) : step === 'done' ? (
          <Card data-testid="consent-link-done">
            <CardContent className="p-6 sm:p-8 text-center space-y-4">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
              {/* Con varios, que se lea que están TODOS (2/10/2026). */}
              <h2 className="text-2xl font-display font-semibold text-[#13231E]" data-testid="consent-link-done-titulo">
                {firmadosDelEnlace.length > 1
                  ? `Has firmado los ${firmadosDelEnlace.length} consentimientos`
                  : 'Consentimiento firmado'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {firmadosDelEnlace.length > 1
                  ? 'No queda ninguno por firmar en este enlace. La clínica ha guardado las firmas en su historia clínica.'
                  : 'La clínica ha guardado la firma en su historia clínica.'}
              </p>
              <Retirados retirados={data?.retirados} />
              <div className="flex flex-col gap-2 max-w-sm mx-auto">
                <DescargasFirmadas signed={data?.signed} />
                {volver && (
                  <a
                    href={volver}
                    className="inline-flex items-center justify-center h-10 rounded-md border border-[#2E6E5E] px-4 text-sm font-medium text-[#13231E] hover:bg-[rgba(46,110,94,0.08)]"
                    data-testid="consent-link-volver"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Volver a mis pasos
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="consent-link-review">
            <CardContent className="p-4 sm:p-6 space-y-4">
              <ProgresoDelEnlace signed={firmadosDelEnlace} porFirmar={porFirmar} />
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-[#2E6E5E] mt-1 shrink-0" />
                <div>
                  {totalDelEnlace > 1 && (
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#2E6E5E]" data-testid="consent-link-documento-n">
                      Documento {firmadosDelEnlace.length + 1} de {totalDelEnlace}
                    </p>
                  )}
                  <h2 className="text-xl font-display font-semibold text-[#13231E]">{pending?.template?.name}</h2>
                  <p className="text-sm text-muted-foreground">Paciente: {pending?.patient?.name}</p>
                  {pending?.surgery_date && <p className="text-xs text-muted-foreground">Cirugía: {pending.surgery_date}</p>}
                </div>
              </div>

              <Retirados retirados={data?.retirados} />
              {aviso && (
                <p
                  className={`text-sm flex items-start gap-1 rounded-md border px-3 py-2 ${
                    aviso.tono === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'
                  }`}
                  data-testid="consent-link-aviso"
                >
                  {aviso.tono === 'ok'
                    ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-[2px]" />
                    : <XCircle className="w-4 h-4 shrink-0 mt-[2px]" />}
                  {' '}{aviso.texto}
                </p>
              )}

              {/* El PDF de ESTE documento se ha visto: el `load` del iframe del
                  visor (sube hasta aquí) o, en el móvil, donde no hay iframe,
                  el botón «Abrir el documento completo». */}
              <div
                onLoad={() => pending && setPdfAbierto(pending.consent_id)}
                onClick={(e) => {
                  if (pending && e.target?.closest?.('a[data-testid="consent-link-pdf-abrir"]')) {
                    setPdfAbierto(pending.consent_id);
                  }
                }}
              >
                <VisorPdfPublico url={pdfUrl} testid="consent-link-pdf" />
              </div>

              {step === 'review' ? (
                <div className="space-y-2">
                  <Button
                    className="w-full h-11 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                    onClick={() => setStep('sign')}
                    disabled={!pdfListo}
                    data-testid="consent-link-quiero-firmar"
                  >
                    <FilePen className="w-4 h-4 mr-2" /> He leído el consentimiento y quiero firmar
                  </Button>
                  {!pdfListo && (
                    <p className="text-xs text-muted-foreground text-center" data-testid="consent-link-espera-lectura">
                      Podrás firmar cuando hayas abierto el documento.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Antes: lienzo fijo de 760x260 reducido por CSS → en el móvil
                      el trazo salía a ~0,4x hacia la esquina, lejos del dedo
                      (revisión de enlaces 2026-09-07). */}
                  <FirmaDedo ref={sigRef} testid="consent-link-signature" alturaCls="h-48 sm:h-56" penColor="#13231E" disabled={signing} />
                  {avisoFirma && (
                    <p className="text-sm text-red-600 flex items-start gap-1" data-testid="consent-link-aviso-firma">
                      <XCircle className="w-4 h-4 shrink-0 mt-[2px]" /> {avisoFirma}
                    </p>
                  )}
                  <Button className="w-full h-12 text-base bg-[#2E6E5E] hover:bg-[#1E4A3F]" onClick={firmar} disabled={signing} data-testid="consent-link-firmar">
                    {signing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Firmar consentimiento
                  </Button>
                </div>
              )}
              {volver && (
                <a href={volver} className="block text-center text-xs text-muted-foreground underline" data-testid="consent-link-volver">
                  {/* Volver a mitad no es haber terminado (2/10/2026). */}
                  {totalDelEnlace > 1 && porFirmar.length > 0
                    ? `Volver a mis pasos (${teQuedan(porFirmar.length).toLowerCase()} por firmar)`
                    : 'Volver a mis pasos'}
                </a>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
